/**
 * CP-128: Browse filter/sort state lives in the /cruises query string.
 *
 * The URL is the source of truth. Browse hydrates its chips, the Dates/price
 * panel, and the sort select from it on load (and on back/forward), and every
 * filter or sort change is merged back into it, so reload, back, and share keep
 * the same results. A chip tap patches only its own key; sibling params such
 * as Home's destination + month stay put.
 *
 * URL params: destination, cruise_line, departure_port, ship_name,
 * month (YYYY-MM, from Home search), start_date / end_date (YYYY-MM-DD),
 * duration (1-5 | 6-9 | 10-14 | 15+), cabin (Interior | Oceanview | Balcony | Suite),
 * min_price / max_price, sort, include_past.
 *
 * CP-121 hold: the default sort is unchanged. Price low to high matches the
 * backend default (sort_by=price, sort_order=asc), so it is never written to
 * the URL and never sent as an override.
 */

export const DEFAULT_BROWSE_SORT = 'price-asc';

export const BROWSE_SORT_KEYS = [
  'price-asc',
  'price-desc',
  'best_value-asc',
  'best_value-desc',
  'departure_date-asc',
  'departure_date-desc',
  'duration-asc',
  'duration-desc',
  'name-asc',
  'name-desc'
];

export const BROWSE_DURATION_VALUES = ['1-5', '6-9', '10-14', '15+'];

/**
 * CP-355 Browse cabin filter: the four values /api/cruises?cabin= accepts
 * (routes.BROWSE_CABIN_LABELS; a backend test pins the list). The UI only ever
 * writes these canonical labels.
 */
export const BROWSE_CABIN_VALUES = ['Interior', 'Oceanview', 'Balcony', 'Suite'];

// Spellings the backend maps to a cabin (routes._BROWSE_CABIN_SPELLINGS): trimmed,
// lower-cased, spaces / hyphens / underscores dropped. A shared link may carry
// any of them; it is read as the canonical label so the URL round-trips.
const CABIN_SPELLINGS = {
  interior: 'Interior',
  inside: 'Interior',
  oceanview: 'Oceanview',
  balcony: 'Balcony',
  balconies: 'Balcony',
  veranda: 'Balcony',
  suite: 'Suite',
  suites: 'Suite'
};

/** Canonical cabin label for a query / UI value, or '' when it is not one. */
export function cabinValue(value) {
  const v = firstValue(value);
  if (v === null || v === undefined) return '';
  const compact = String(v).trim().toLowerCase().replace(/[ _-]+/g, '');
  return Object.prototype.hasOwnProperty.call(CABIN_SPELLINGS, compact) ? CABIN_SPELLINGS[compact] : '';
}

// Query keys Browse owns. Anything else (utm_*, etc.) is carried through.
export const BROWSE_QUERY_KEYS = [
  'destination',
  'cruise_line',
  'departure_port',
  'ship_name',
  'month',
  'start_date',
  'end_date',
  'duration',
  'cabin',
  'min_price',
  'max_price',
  'sort',
  'include_past'
];

const CATEGORICAL_KEYS = ['destination', 'cruise_line', 'departure_port', 'ship_name'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

function firstValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function queryString(value) {
  const v = firstValue(value);
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function validDate(value) {
  const s = queryString(value);
  if (!DATE_RE.test(s)) return '';
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return '';
  return s;
}

function priceValue(value) {
  const v = firstValue(value);
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** First/last day of a YYYY-MM month, or null for anything else. */
export function monthRange(month) {
  const match = MONTH_RE.exec(queryString(month));
  if (!match) return null;
  const year = Number(match[1]);
  const mon = Number(match[2]);
  const lastDay = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  return {
    start_date: `${match[1]}-${match[2]}-01`,
    end_date: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`
  };
}

/** YYYY-MM when start/end cover exactly one calendar month, else ''. */
export function monthForRange(startDate, endDate) {
  if (!startDate || !endDate) return '';
  const month = String(startDate).slice(0, 7);
  const range = monthRange(month);
  if (!range) return '';
  return range.start_date === startDate && range.end_date === endDate ? month : '';
}

/** Duration chip value → API min/max nights. */
export function durationBounds(duration) {
  if (duration === '15+') return { min_duration: 15, max_duration: null };
  if (BROWSE_DURATION_VALUES.includes(duration)) {
    const [min, max] = duration.split('-').map(Number);
    return { min_duration: min, max_duration: max };
  }
  return { min_duration: null, max_duration: null };
}

/** Empty Browse state (what Reset returns to). Sort stays the shipped default. */
export function emptyBrowseState() {
  return {
    destination: '',
    cruise_line: '',
    departure_port: '',
    ship_name: '',
    start_date: '',
    end_date: '',
    duration: '',
    cabin: '',
    min_price: null,
    max_price: null,
    sort: DEFAULT_BROWSE_SORT,
    include_past: false
  };
}

/**
 * Decode the /cruises query into Browse UI state. Explicit start_date /
 * end_date win over Home's month; unknown or malformed values fall back to
 * empty (never invented).
 */
export function browseStateFromQuery(query) {
  const q = query && typeof query === 'object' ? query : {};
  const state = emptyBrowseState();
  CATEGORICAL_KEYS.forEach((key) => {
    state[key] = queryString(q[key]);
  });
  const fromMonth = monthRange(q.month) || { start_date: '', end_date: '' };
  state.start_date = validDate(q.start_date) || fromMonth.start_date;
  state.end_date = validDate(q.end_date) || fromMonth.end_date;
  const duration = queryString(q.duration);
  state.duration = BROWSE_DURATION_VALUES.includes(duration) ? duration : '';
  state.cabin = cabinValue(q.cabin);
  state.min_price = priceValue(q.min_price);
  state.max_price = priceValue(q.max_price);
  const sort = queryString(q.sort);
  // CP-101: ?sort=best_value (probe / share shorthand) → best_value-asc.
  if (sort === 'best_value') {
    state.sort = 'best_value-asc';
  } else {
    state.sort = BROWSE_SORT_KEYS.includes(sort) ? sort : DEFAULT_BROWSE_SORT;
  }
  state.include_past = false; // CP-165: past sailings never shown; old links ignored
  return state;
}

/**
 * Merge a (partial) Browse state patch into an existing query. Only keys in
 * the patch change; every sibling filter and any non-Browse param is kept.
 * Returns a fresh, canonical query object for router.replace / router.push.
 */
export function mergeBrowseQuery(currentQuery, patch) {
  const current = currentQuery && typeof currentQuery === 'object' ? currentQuery : {};
  const state = { ...browseStateFromQuery(current), ...(patch || {}) };
  const out = {};
  Object.keys(current).forEach((key) => {
    if (!BROWSE_QUERY_KEYS.includes(key)) out[key] = current[key];
  });

  CATEGORICAL_KEYS.forEach((key) => {
    const v = queryString(state[key]);
    if (v) out[key] = v;
  });

  const start = validDate(state.start_date);
  const end = validDate(state.end_date);
  const month = monthForRange(start, end);
  if (month) {
    // Keep Home's compact month param while the range is still that month.
    out.month = month;
  } else {
    if (start) out.start_date = start;
    if (end) out.end_date = end;
  }

  if (BROWSE_DURATION_VALUES.includes(state.duration)) out.duration = state.duration;

  const cabin = cabinValue(state.cabin);
  if (cabin) out.cabin = cabin;

  const minPrice = priceValue(state.min_price);
  const maxPrice = priceValue(state.max_price);
  if (minPrice !== null) out.min_price = String(minPrice);
  if (maxPrice !== null) out.max_price = String(maxPrice);

  if (BROWSE_SORT_KEYS.includes(state.sort) && state.sort !== DEFAULT_BROWSE_SORT) {
    out.sort = state.sort;
  }
  return out;
}

/** /api/cruises filter params for a Browse query (no page/per_page). */
export function apiFiltersFromQuery(query) {
  const state = browseStateFromQuery(query);
  const params = {};
  CATEGORICAL_KEYS.forEach((key) => {
    if (state[key]) params[key] = state[key];
  });
  if (state.start_date) params.start_date = state.start_date;
  if (state.end_date) params.end_date = state.end_date;
  const { min_duration, max_duration } = durationBounds(state.duration);
  if (min_duration !== null) params.min_duration = min_duration;
  if (max_duration !== null) params.max_duration = max_duration;
  // CP-355: only when a cabin is picked, so a no-cabin request is exactly what it was.
  if (state.cabin) params.cabin = state.cabin;
  if (state.min_price !== null) params.min_price = state.min_price;
  if (state.max_price !== null) params.max_price = state.max_price;
  if (state.sort !== DEFAULT_BROWSE_SORT) {
    const cut = state.sort.lastIndexOf('-');
    params.sort_by = state.sort.slice(0, cut);
    params.sort_order = state.sort.slice(cut + 1);
  }
  return params;
}

/** Stable comparison key for two Browse queries (ignores non-Browse params). */
export function browseQueryKey(query) {
  const canonical = mergeBrowseQuery(query, {});
  return BROWSE_QUERY_KEYS.filter((key) => key in canonical)
    .map((key) => `${key}=${canonical[key]}`)
    .join('&');
}
